// Servicio API base para Inside CRM.
// Auth + Usuarios + Audit log + Referencia + Clientes + Contratos + Catálogos + Municipios

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Resuelve URLs relativas que sirve el backend (p.ej. /api/entidades/logos/xxx)
// a URL absoluta. Deja pasar http(s) externos sin tocar.
export const resolveAssetUrl = (path) => {
  if (!path) return null;
  const s = String(path);
  if (s.startsWith('http://') || s.startsWith('https://') || s.startsWith('data:')) return s;
  if (s.startsWith('/api/') || s.startsWith('/uploads/')) return `${API_URL}${s}`;
  return s;
};

const getAuthHeaders = () => {
  const token = localStorage.getItem('token');
  return {
    'Content-Type': 'application/json',
    ...(token ? { Authorization: `Bearer ${token}` } : {}),
  };
};

async function parseBody(res) {
  try {
    return await res.clone().json();
  } catch (_) {
    try {
      const txt = await res.clone().text();
      if (!txt) return null;
      try { return JSON.parse(txt); } catch (_) { return { detail: txt }; }
    } catch (_) { return null; }
  }
}

async function request(path, options = {}) {
  const res = await fetch(`${API_URL}${path}`, {
    ...options,
    headers: { ...getAuthHeaders(), ...(options.headers || {}) },
  });
  if (!res.ok) {
    const body = await parseBody(res);
    const detail =
      (body && (body.detail || body.message || body.error)) || `Error ${res.status}`;
    throw new Error(typeof detail === 'string' ? detail : JSON.stringify(detail));
  }
  if (res.status === 204) return null;
  return res.json();
}

/**
 * Wrapper `req()` reusable por todas las páginas/componentes.
 * Iter58 — DRY: sustituye 13 implementaciones locales casi idénticas.
 *
 * Acepta opciones extendidas:
 *   - body: objeto → se serializa a JSON con Content-Type
 *   - body: FormData → se envía sin sobrescribir headers (multipart auto)
 *   - body: string → se envía tal cual (Content-Type queda al caller)
 *
 * Devuelve el JSON parseado o null si 204.
 */
export async function req(path, options = {}) {
  const opts = { ...options };
  if (opts.body && typeof opts.body === 'object' && !(opts.body instanceof FormData) && !(opts.body instanceof Blob)) {
    opts.headers = { 'Content-Type': 'application/json', ...(opts.headers || {}) };
    opts.body = JSON.stringify(opts.body);
  }
  return request(path, opts);
}

const qs = (params) => {
  const clean = Object.fromEntries(
    Object.entries(params || {}).filter(([, v]) => v !== undefined && v !== null && v !== '')
  );
  const s = new URLSearchParams(clean).toString();
  return s ? `?${s}` : '';
};

// === Auth ===
export const authApi = {
  login: (email, password) =>
    request('/api/auth/login', { method: 'POST', body: JSON.stringify({ email, password }) }),
  getMe: () => request('/api/auth/me'),
  updateMe: (data) => request('/api/auth/me', { method: 'PUT', body: JSON.stringify(data) }),
  logout: () => request('/api/auth/logout', { method: 'POST' }),
};

// === Noticias sector (Monitor Sectorial) ===
export const noticiasApi = {
  list: (params) => req(`/api/noticias-sector${qs(params)}`),
  valorar: (id, valoracion) =>
    req(`/api/noticias-sector/${id}/valorar`, { method: 'POST', body: { valoracion } }),
};

// === Datos del sector agua (INE + AEAS) ===
export const datosSectorApi = {
  nacional: () => req('/api/datos-sector/nacional'),
  ccaa: () => req('/api/datos-sector/ccaa'),
  ccaaByCodigo: (codigo) => req(`/api/datos-sector/ccaa/${codigo}`),
};

// === Usuarios (admin only) ===
export const usuariosApi = {
  list: (filters) => request(`/api/usuarios${qs(filters)}`),
  seleccionables: () => request('/api/usuarios/seleccionables'),
  get: (id) => request(`/api/usuarios/${id}`),
  create: (data) => request('/api/usuarios', { method: 'POST', body: JSON.stringify(data) }),
  update: (id, data) => request(`/api/usuarios/${id}`, { method: 'PUT', body: JSON.stringify(data) }),
  resetPassword: (id, password) =>
    request(`/api/usuarios/${id}/password`, { method: 'POST', body: JSON.stringify({ password }) }),
  deactivate: (id) => request(`/api/usuarios/${id}`, { method: 'DELETE' }),
};

// === Grupos empresariales ===
export const gruposApi = {
  list: () => request('/api/grupos'),
  detalle: (id) => request(`/api/grupos/${id}/detalle`),  // Iter61
  create: (data) => request('/api/grupos', { method: 'POST', body: JSON.stringify(data) }),
  update: (id, data) => request(`/api/grupos/${id}`, { method: 'PUT', body: JSON.stringify(data) }),
  delete: (id) => request(`/api/grupos/${id}`, { method: 'DELETE' }),
};

// === Zonas (agrupación provincias + cobertura temporal) ===
export const zonasApi = {
  list: () => request('/api/zonas'),
  create: (data) => request('/api/zonas', { method: 'POST', body: JSON.stringify(data) }),
  update: (id, data) => request(`/api/zonas/${id}`, { method: 'PUT', body: JSON.stringify(data) }),
  delete: (id) => request(`/api/zonas/${id}`, { method: 'DELETE' }),
  cubrir: (id, payload) => request(`/api/zonas/${id}/cubrir`, { method: 'POST', body: JSON.stringify(payload) }),
  devolver: (id) => request(`/api/zonas/${id}/devolver`, { method: 'POST' }),
  asignarAUsuario: (userId, zonaIds) =>
    request(`/api/zonas/usuario/${userId}`, { method: 'PUT', body: JSON.stringify({ zona_ids: zonaIds }) }),
};

// === Entidades (clientes) ===
export const entidadesApi = {
  list: (params = {}) => request(`/api/entidades${qs(params)}`),
  paginated: (params = {}) => request(`/api/entidades/paginated${qs(params)}`),
  autocomplete: (q, limit = 10) => request(`/api/entidades/autocomplete?q=${encodeURIComponent(q)}&limit=${limit}`),
  get: (id) => request(`/api/entidades/${id}`),
  actividad: (id, limit = 3) => request(`/api/entidades/${id}/actividad?limit=${limit}`),
  delegaciones: (id) => request(`/api/entidades/${id}/delegaciones`),
  analisisInactividad: (dias = 1095) => request(`/api/entidades/analisis/inactividad?dias=${dias}`),
  historial: (id, limit = 50) => request(`/api/entidades/${id}/historial?limit=${limit}`),
  marcarInactivo: (id) => request(`/api/entidades/${id}/marcar-inactivo`, { method: 'PATCH' }),
  create: (data) => request('/api/entidades', { method: 'POST', body: JSON.stringify(data) }),
  update: (id, data) => request(`/api/entidades/${id}`, { method: 'PUT', body: JSON.stringify(data) }),
  remove: (id) => request(`/api/entidades/${id}`, { method: 'DELETE' }),
  deactivate: (id) => request(`/api/entidades/${id}`, { method: 'DELETE' }),
  subirLogo: (id, formData) =>
    fetch(`${API_URL}/api/entidades/${id}/logo`, {
      method: 'POST',
      headers: { Authorization: `Bearer ${localStorage.getItem('token')}` },
      body: formData,
    }).then(async (r) => { if (!r.ok) throw new Error((await r.json().catch(()=>({}))).detail || 'Error al subir logo'); return r.json(); }),
  borrarLogo: (id) => request(`/api/entidades/${id}/logo`, { method: 'DELETE' }),
  zonasDisponibles: () => request('/api/entidades/zonas-disponibles'),
  uteSocios: (id) => request(`/api/entidades/${id}/ute-socios`),
  utesDondeParticipo: (id) => request(`/api/entidades/${id}/utes-donde-participo`),
};

// === Contactos ===
export const contactosApi = {
  list: (filters) => request(`/api/contactos${qs(filters)}`),
  get: (id) => request(`/api/contactos/${id}`),
  create: (data) => request('/api/contactos', { method: 'POST', body: JSON.stringify(data) }),
  update: (id, data) => request(`/api/contactos/${id}`, { method: 'PUT', body: JSON.stringify(data) }),
  delete: (id) => request(`/api/contactos/${id}`, { method: 'DELETE' }),
  cambiarEmpresa: (id, payload) =>
    request(`/api/contactos/${id}/cambiar-empresa`, { method: 'POST', body: JSON.stringify(payload) }),
};

// === Contratos de gestión ===
export const contratosApi = {
  list: (filters) => request(`/api/contratos${qs(filters)}`),
  get: (id) => request(`/api/contratos/${id}`),
  create: (data) => request('/api/contratos', { method: 'POST', body: JSON.stringify(data) }),
  update: (id, data) => request(`/api/contratos/${id}`, { method: 'PUT', body: JSON.stringify(data) }),
  delete: (id) => request(`/api/contratos/${id}`, { method: 'DELETE' }),
  cambiarGestora: (id, payload) =>
    request(`/api/contratos/${id}/cambiar-gestora`, { method: 'POST', body: JSON.stringify(payload) }),
  // Avisos por contrato
  listAvisos: (cid) => request(`/api/contratos/${cid}/avisos`),
  crearAviso: (cid, data) =>
    request(`/api/contratos/${cid}/avisos`, { method: 'POST', body: JSON.stringify(data) }),
};

// === Avisos (global, por usuario) ===
export const avisosApi = {
  proximos: (dias = 60) => request(`/api/avisos/proximos?dias=${dias}`),
  update: (id, data) => request(`/api/avisos/${id}`, { method: 'PUT', body: JSON.stringify(data) }),
  delete: (id) => request(`/api/avisos/${id}`, { method: 'DELETE' }),
};

// === Visitas ===
export const visitasApi = {
  list: (filters) => request(`/api/visitas${qs(filters)}`),
  get: (id) => request(`/api/visitas/${id}`),
  create: (entidadId, data) =>
    request(`/api/visitas/entidad/${entidadId}`, { method: 'POST', body: JSON.stringify(data) }),
  // Iter60 — creación global (entidad_id opcional dentro del body)
  createGlobal: (data) =>
    request('/api/visitas', { method: 'POST', body: JSON.stringify(data) }),
  update: (id, data) => request(`/api/visitas/${id}`, { method: 'PUT', body: JSON.stringify(data) }),
  delete: (id) => request(`/api/visitas/${id}`, { method: 'DELETE' }),
};

// === Tareas / To-do ===
export const tareasApi = {
  list: (filters) => request(`/api/tareas${qs(filters)}`),
  create: (data) => request('/api/tareas', { method: 'POST', body: JSON.stringify(data) }),
  update: (id, data) => request(`/api/tareas/${id}`, { method: 'PUT', body: JSON.stringify(data) }),
  delete: (id) => request(`/api/tareas/${id}`, { method: 'DELETE' }),
};

// === Municipios INE ===
export const municipiosApi = {
  list: (filters) => request(`/api/municipios${qs(filters)}`),
  mapa: (filters) => request(`/api/municipios/mapa${qs(filters)}`),
  porCp: (cp, pais = 'es') => request(`/api/municipios/por-cp?cp=${encodeURIComponent(cp)}&pais=${pais}`),
  autocompleteDireccion: (q, pais = 'es') => request(`/api/municipios/autocomplete-direccion?q=${encodeURIComponent(q)}&pais=${pais}`),
  listByProvincia: (provincia) => {
    const params = new URLSearchParams({ provincia, limit: '2000' });
    return request(`/api/municipios?${params.toString()}`);
  },
};

// === Catálogos editables ===
export const catalogosApi = {
  list: (tipo) => request(`/api/catalogos/${tipo}`),
  create: (tipo, data) => request(`/api/catalogos/${tipo}`, { method: 'POST', body: JSON.stringify(data) }),
  update: (tipo, valor, data) =>
    request(`/api/catalogos/${tipo}/${valor}`, { method: 'PUT', body: JSON.stringify(data) }),
  delete: (tipo, valor) => request(`/api/catalogos/${tipo}/${valor}`, { method: 'DELETE' }),
};

// === Productos (Fase 2g) ===
export const productosApi = {
  list: (filters) => request(`/api/productos${qs(filters)}`),
  get: (id) => request(`/api/productos/${id}`),
  create: (data) => request('/api/productos', { method: 'POST', body: JSON.stringify(data) }),
  update: (id, data) => request(`/api/productos/${id}`, { method: 'PUT', body: JSON.stringify(data) }),
  delete: (id) => request(`/api/productos/${id}`, { method: 'DELETE' }),
  // Importación CSV
  importCampos: () => request('/api/productos/_import/campos'),
  importDetectar: (formData) =>
    fetch(`${API_URL}/api/productos/_import/detectar`, {
      method: 'POST',
      headers: { Authorization: `Bearer ${localStorage.getItem('token')}` },
      body: formData,
    }).then(async (r) => { if (!r.ok) throw new Error((await r.json()).detail || 'Error'); return r.json(); }),
  importar: (formData) =>
    fetch(`${API_URL}/api/productos/_import`, {
      method: 'POST',
      headers: { Authorization: `Bearer ${localStorage.getItem('token')}` },
      body: formData,
    }).then(async (r) => { if (!r.ok) throw new Error((await r.json()).detail || 'Error'); return r.json(); }),
};

// === Importación ERP (PDF facturas + CSV pedidos) ===
export const importacionErpApi = {
  resumen: () => request('/api/erp/resumen-importacion'),
  estadoJob: (jobId) => request(`/api/erp/importar-job/${jobId}`),
  importarPdf: (file, dryRun = false) => {
    const fd = new FormData();
    fd.append('archivo', file);
    fd.append('dry_run', dryRun ? 'true' : 'false');
    return fetch(`${API_URL}/api/erp/importar-facturas-pdf`, {
      method: 'POST',
      headers: { Authorization: `Bearer ${localStorage.getItem('token')}` },
      body: fd,
    }).then(async (r) => { if (!r.ok) throw new Error((await r.json()).detail || 'Error'); return r.json(); });
  },
  importarCsv: (file, dryRun = false) => {
    const fd = new FormData();
    fd.append('archivo', file);
    fd.append('dry_run', dryRun ? 'true' : 'false');
    return fetch(`${API_URL}/api/erp/importar-pedidos-csv`, {
      method: 'POST',
      headers: { Authorization: `Bearer ${localStorage.getItem('token')}` },
      body: fd,
    }).then(async (r) => { if (!r.ok) throw new Error((await r.json()).detail || 'Error'); return r.json(); });
  },
};

// === Artículos ERP (clasificación familias/subfamilias) ===
export const articulosApi = {
  list:  (filters) => request(`/api/articulos${qs(filters)}`),
  familias: () => request('/api/articulos/familias'),
  update: (codigo, data) => request(`/api/articulos/${encodeURIComponent(codigo)}`, { method: 'PUT', body: JSON.stringify(data) }),
  clasificarMasivo: (data) => request('/api/articulos/clasificar-masivo', { method: 'POST', body: JSON.stringify(data) }),
  clasificarDesdeDocumentos: () => request('/api/articulos/clasificar-desde-documentos', { method: 'POST' }),
};

// === Homologaciones (Fase 2g) ===
export const homologacionesApi = {
  list: (filters) => request(`/api/homologaciones${qs(filters)}`),
  get: (id) => request(`/api/homologaciones/${id}`),
  create: (data) => request('/api/homologaciones', { method: 'POST', body: JSON.stringify(data) }),
  update: (id, data) => request(`/api/homologaciones/${id}`, { method: 'PUT', body: JSON.stringify(data) }),
  delete: (id) => request(`/api/homologaciones/${id}`, { method: 'DELETE' }),
  archivos: (id) => request(`/api/homologaciones/${id}/archivos`),
  subirArchivo: (id, formData) =>
    fetch(`${API_URL}/api/homologaciones/${id}/archivos`, {
      method: 'POST',
      headers: { Authorization: `Bearer ${localStorage.getItem('token')}` },
      body: formData,
    }).then(async (r) => { if (!r.ok) throw new Error((await r.json().catch(()=>({}))).detail || 'Error al subir'); return r.json(); }),
  descargarArchivo: (archivoId) => `${API_URL}/api/homologaciones/archivos/${archivoId}/descargar`,
  borrarArchivo: (archivoId) => request(`/api/homologaciones/archivos/${archivoId}`, { method: 'DELETE' }),
};

// === Proyectos (Fase 2g) ===
export const proyectosApi = {
  list: (filters) => request(`/api/proyectos${qs(filters)}`),
  get: (id) => request(`/api/proyectos/${id}`),
  create: (data) => request('/api/proyectos', { method: 'POST', body: JSON.stringify(data) }),
  update: (id, data) => request(`/api/proyectos/${id}`, { method: 'PUT', body: JSON.stringify(data) }),
  delete: (id) => request(`/api/proyectos/${id}`, { method: 'DELETE' }),
};

// === Campos personalizados (Fase 2g) ===
export const camposConfigApi = {
  list: (filters) => request(`/api/campos-config${qs(filters)}`),
  create: (data) => request('/api/campos-config', { method: 'POST', body: JSON.stringify(data) }),
  update: (id, data) => request(`/api/campos-config/${id}`, { method: 'PUT', body: JSON.stringify(data) }),
  delete: (id) => request(`/api/campos-config/${id}`, { method: 'DELETE' }),
};

// === Audit log (admin only) ===
export const auditApi = {
  list: (filters) => request(`/api/audit-log${qs(filters)}`),
  count: (filters) => request(`/api/audit-log/count${qs(filters)}`),
};

// === Referencia ===
export const referenciaApi = {
  provincias: () => request('/api/referencia/provincias'),
  provinciasPorPais: () => request('/api/referencia/provincias/por_pais'),
  paises: (incluirIberia = false) => request(`/api/referencia/paises${incluirIberia ? '?incluir_iberia=true' : ''}`),
};

// === Export CSV (admin/dir_comercial only) ===
const API_URL_EXPORT = process.env.REACT_APP_BACKEND_URL;
export const exportApi = {
  descargar: async (tipo) => {
    const res = await fetch(`${API_URL_EXPORT}/api/export/${tipo}`, {
      headers: { Authorization: `Bearer ${localStorage.getItem('token')}` },
    });
    if (!res.ok) throw new Error((await res.json().catch(() => ({}))).detail || 'Error de exportación');
    const blob = await res.blob();
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `${tipo}.csv`;
    document.body.appendChild(a);
    a.click();
    a.remove();
    URL.revokeObjectURL(url);
  },
};

// === Asistente IA (Gemini + Perplexity) — /api/ia ===
export const iaApi = {
  health: () => request('/api/ia/health'),
  memoria: {
    get: () => request('/api/ia/memoria'),
    update: (data) => request('/api/ia/memoria', { method: 'PATCH', body: JSON.stringify(data) }),
  },
  corregirTexto: (texto) => request('/api/ia/corregir-texto', { method: 'POST', body: JSON.stringify({ texto }) }),
  reescribir: (texto, estilo = 'profesional') => request('/api/ia/reescribir', { method: 'POST', body: JSON.stringify({ texto, estilo }) }),
  traducir: (texto, idioma_destino) => request('/api/ia/traducir', { method: 'POST', body: JSON.stringify({ texto, idioma_destino }) }),
  buscarEmpresa: (payload) => request('/api/ia/buscar-empresa', { method: 'POST', body: JSON.stringify(payload) }),
  analizarVisita: (payload) => request('/api/ia/analizar-visita', { method: 'POST', body: JSON.stringify(payload) }),
  analizarCliente: (entidad_id) => request('/api/ia/analizar-cliente', { method: 'POST', body: JSON.stringify({ entidad_id }) }),
  detectarDuplicado: (payload) => request('/api/ia/detectar-duplicado-contacto', { method: 'POST', body: JSON.stringify(payload) }),
  chat: (payload) => request('/api/ia/chat', { method: 'POST', body: JSON.stringify(payload) }),
  historial: (sessionId) => request(`/api/ia/chat/${sessionId}`),
  ejecutar: (acciones, dry_run = false) => request('/api/ia/ejecutar', { method: 'POST', body: JSON.stringify({ acciones, dry_run }) }),
};

// Alias de compatibilidad (frontend actual usa aiApi)
export const aiApi = iaApi;

// === Health ===
export const healthApi = {
  check: () => request('/api/health'),
};

export const perplexityApi = {
  buscarEmpresa: (data) => request('/api/perplexity/buscar-empresa', { method: 'POST', body: JSON.stringify(data) }),
  buscarPersona: (data) => request('/api/perplexity/buscar-persona', { method: 'POST', body: JSON.stringify(data) }),
  enriquecerMasivo: (data) => request('/api/perplexity/enriquecer-masivo', { method: 'POST', body: JSON.stringify(data) }),
  enriquecerContactosMasivo: (data) => request('/api/perplexity/contactos/enriquecer-masivo', { method: 'POST', body: JSON.stringify(data) }),
  getJob: (id) => request(`/api/perplexity/jobs/${id}`),
  aplicarResoluciones: (id, resoluciones) => request(`/api/perplexity/jobs/${id}/aplicar`, { method: 'POST', body: JSON.stringify({ resoluciones }) }),
  logosClearbitMasivo: (sobrescribir = false, limit = 2000, usarAvatarFallback = true, entidadesIds = null) =>
    request(
      `/api/perplexity/logos/clearbit-masivo?sobrescribir=${sobrescribir}&limit=${limit}&usar_avatar_fallback=${usarAvatarFallback}`,
      {
        method: 'POST',
        body: JSON.stringify({ entidades_ids: entidadesIds && entidadesIds.length ? entidadesIds : null }),
      },
    ),
};

// === Sprint S4 — Permisos configurables ===
export const permisosApi = {
  catalogo: () => request('/api/admin/permisos/catalogo'),
  matriz: () => request('/api/admin/permisos/matriz'),
  actualizar: (rol, recurso, payload) =>
    request(`/api/admin/permisos/${rol}/${recurso}`, { method: 'PUT', body: JSON.stringify(payload) }),
  resetDefaults: () => request('/api/admin/permisos/reset-defaults', { method: 'POST' }),
};

// === Feature Flags ===
export const flagsApi = {
  list: () => request('/api/feature-flags'),
  definitions: () => request('/api/feature-flags/definitions'),
  update: (key, enabled) =>
    request(`/api/feature-flags/${key}`, { method: 'PUT', body: JSON.stringify({ enabled }) }),
};

// === Sprint S4 — Scheduler ===
export const schedulerApi = {
  list: () => request('/api/scheduler/config'),
  update: (jobId, payload) =>
    request(`/api/scheduler/config/${jobId}`, { method: 'PUT', body: JSON.stringify(payload) }),
};

// === Sprint S4 — ERP Sync ===
export const erpSyncApi = {
  listConfigs: () => request('/api/erp/config'),
  createConfig: (data) => request('/api/erp/config', { method: 'POST', body: JSON.stringify(data) }),
  updateConfig: (id, data) => request(`/api/erp/config/${id}`, { method: 'PUT', body: JSON.stringify(data) }),
  deleteConfig: (id) => request(`/api/erp/config/${id}`, { method: 'DELETE' }),
  logs: (limit = 100) => request(`/api/erp/logs?limit=${limit}`),
  colaRevision: (filters = {}) => request(`/api/erp/cola-revision${qs(filters)}`),
  asignarCola: (id, data) => request(`/api/erp/cola-revision/${id}/asignar`, { method: 'POST', body: JSON.stringify(data) }),
  listMapeos: () => request('/api/erp/mapeo-referencias'),
  crearMapeo: (data) => request('/api/erp/mapeo-referencias', { method: 'POST', body: JSON.stringify(data) }),
  dryRun: (formData) =>
    fetch(`${API_URL}/api/erp/dry-run`, {
      method: 'POST',
      headers: { Authorization: `Bearer ${localStorage.getItem('token')}` },
      body: formData,
    }).then(async (r) => { if (!r.ok) throw new Error((await r.json()).detail || 'Error'); return r.json(); }),
};

// === Sprint S4.5 — Documentos comerciales (Inology: ofertas/pedidos/albaranes/facturas) ===
export const docsComercialesApi = {
  list: (filters = {}) => request(`/api/documentos-comerciales${qs(filters)}`),
  estadoPedidos: (params = {}) => request(`/api/documentos-comerciales/estado-pedidos${qs(params)}`),
  anularDocumento: (id) => request(`/api/documentos-comerciales/${id}/anular`, { method: 'PATCH' }),
  detalle: (id) => request(`/api/documentos-comerciales/${id}`),
  trazabilidad: (id) => request(`/api/documentos-comerciales/${id}/trazabilidad`),
  metricas: (filters = {}) => request(`/api/documentos-comerciales/metricas/resumen${qs(filters)}`),
  importar: (formData, dryRun = false, empresa = null) => {
    const params = new URLSearchParams({ dry_run: dryRun });
    if (empresa) params.set('empresa', empresa);
    return fetch(`${API_URL}/api/documentos-comerciales/importar?${params.toString()}`, {
      method: 'POST',
      headers: { Authorization: `Bearer ${localStorage.getItem('token')}` },
      body: formData,
    }).then(async (r) => { if (!r.ok) throw new Error((await r.json()).detail || 'Error'); return r.json(); });
  },
  importarStatus: (jobId) => request(`/api/documentos-comerciales/importar/${jobId}/status`),
};

// === Sprint S5 — Ficha Cliente 360° ===
export const fichaClienteApi = {
  resumen: (entidadId) => request(`/api/documentos-comerciales/cliente/${entidadId}/resumen`),
  facturacion: (entidadId) => request(`/api/documentos-comerciales/cliente/${entidadId}/facturacion`),
  articulos: (entidadId) => request(`/api/documentos-comerciales/cliente/${entidadId}/articulos`),
  pedidosEstado: (entidadId) => request(`/api/documentos-comerciales/cliente/${entidadId}/pedidos-estado`),
};

// === ERP — Depósitos ===
export const depositosApi = {
  list: (params = {}) => request(`/api/depositos${qs(params)}`),
  resumen: () => request('/api/depositos/resumen'),
  stock: (codigo, incluirAgotados = false) =>
    request(`/api/depositos/${codigo}/stock?incluir_agotados=${incluirAgotados}`),
};

// === ERP — Tarifas cliente ===
export const tarifasApi = {
  list: (params = {}) => request(`/api/tarifas${qs(params)}`),
  activa: (entidadId, empresa) => request(`/api/tarifas/${entidadId}?empresa=${empresa}`),
  crear: (data) => request('/api/tarifas', { method: 'POST', body: JSON.stringify(data) }),
  actualizar: (id, data) => request(`/api/tarifas/${id}`, { method: 'PUT', body: JSON.stringify(data) }),
  eliminar: (id) => request(`/api/tarifas/${id}`, { method: 'DELETE' }),
  inferir: (entidadId, empresa) => request(`/api/tarifas/inferir-desde-historico/${entidadId}?empresa=${empresa}`),
};

// === Analytics agregados — Dashboard Ventas y Métricas ===
export const dashboardOperativoApi = {
  kpis: () => request('/api/dashboard/operativo'),
};

export const analyticsComercialesApi = {
  coberturaDatos: () => request('/api/documentos-comerciales/analytics/cobertura-datos'),
  resumenGeneral: (empresa, desde, hasta) => request(`/api/documentos-comerciales/analytics/resumen-general${qs({ empresa, desde, hasta })}`),
  topClientes: (limit = 10, empresa, desde, hasta) => request(`/api/documentos-comerciales/analytics/top-clientes${qs({ limit, empresa, desde, hasta })}`),
  clientesRiesgo: (dias = 90, limit = 20, empresa, desde, hasta) => request(`/api/documentos-comerciales/analytics/clientes-riesgo${qs({ dias, limit, empresa, desde, hasta })}`),
  embudoConversion: (empresa, desde, hasta) => request(`/api/documentos-comerciales/analytics/embudo-conversion${qs({ empresa, desde, hasta })}`),
  parcialidadClientes: (params = {}) => request(`/api/documentos-comerciales/analytics/parcialidad-clientes${qs(params)}`),
  anioVsAnio: (empresa, familia) => request(`/api/documentos-comerciales/analytics/anio-vs-anio${qs({ empresa, familia })}`),
  rankingComerciales: (limit = 20, empresa, desde, hasta) => request(`/api/documentos-comerciales/analytics/ranking-comerciales${qs({ limit, empresa, desde, hasta })}`),
  porFamilia: (empresa, desde, hasta, entidad_id) => request(`/api/documentos-comerciales/analytics/por-familia${qs({ empresa, desde, hasta, entidad_id })}`),
  penetracionCliente: (entidad_id, desde, hasta) => request(`/api/documentos-comerciales/analytics/penetracion-cliente/${entidad_id}${qs({ desde, hasta })}`),
};

// === Sprint S4.5 — Bots IA ===
export const botsIaApi = {
  plantillas: () => request('/api/bots-ia/plantillas'),
  list: () => request('/api/bots-ia'),
  crear: (data) => request('/api/bots-ia', { method: 'POST', body: JSON.stringify(data) }),
  crearDesdePlantilla: (clave) => request(`/api/bots-ia/desde-plantilla/${clave}`, { method: 'POST' }),
  actualizar: (id, data) => request(`/api/bots-ia/${id}`, { method: 'PUT', body: JSON.stringify(data) }),
  eliminar: (id) => request(`/api/bots-ia/${id}`, { method: 'DELETE' }),
  ejecutarAhora: (id) => request(`/api/bots-ia/${id}/ejecutar-ahora`, { method: 'POST' }),
  logs: (id, limit = 50) => request(`/api/bots-ia/${id}/logs?limit=${limit}`),
};

// === Sprint S4.5 — Dashboard configurable ===
export const dashboardApi = {
  widgetsRegistry: () => request('/api/dashboard/widgets-registry'),
  getConfig: (rol) => request(`/api/dashboard/config/${rol}`),
  setConfig: (rol, widgets) =>
    request(`/api/dashboard/config/${rol}`, { method: 'PUT', body: JSON.stringify({ widgets }) }),
  miDashboard: () => request('/api/dashboard/mi-dashboard'),
};

// === Lote F7 — Plan Semanal IA ===
export const planSemanalApi = {
  generar: (forzar = false) =>
    request(`/api/plan-semanal/generar${forzar ? '?forzar=true' : ''}`, { method: 'POST' }),
  actual: () => request('/api/plan-semanal/actual'),
  equipo: () => request('/api/plan-semanal/equipo'),
  cambiarEstado: (planId, propuestaId, estado) =>
    request(`/api/plan-semanal/${planId}/propuesta/${propuestaId}/estado?estado=${estado}`, { method: 'POST' }),
  informePdfUrl: () => `${API_URL}/api/plan-semanal/informe-pdf`,
  getConfig: () => request('/api/plan-semanal/configuracion'),
  setConfig: ({ proactivo, email }) => {
    const qs = new URLSearchParams();
    if (proactivo !== undefined) qs.set('proactivo', proactivo);
    if (email !== undefined) qs.set('email', email);
    return request(`/api/plan-semanal/configuracion?${qs.toString()}`, { method: 'PATCH' });
  },
  enviarEmail: (destinatario) =>
    request(`/api/plan-semanal/enviar-email${destinatario ? `?destinatario=${encodeURIComponent(destinatario)}` : ''}`, { method: 'POST' }),
};

// ============================================================
// Pedidos (Módulo B — OCR + matching + propuesta oferta)
// ============================================================
export const pedidosApi = {
  list: (params = {}) => {
    const qs = new URLSearchParams();
    if (params.estado) qs.set('estado', params.estado);
    if (params.entidad_id) qs.set('entidad_id', params.entidad_id);
    if (params.q) qs.set('q', params.q);
    if (params.skip != null) qs.set('skip', params.skip);
    if (params.limit != null) qs.set('limit', params.limit);
    return request(`/api/pedidos${qs.toString() ? `?${qs.toString()}` : ''}`);
  },
  get: (id) => request(`/api/pedidos/${id}`),
  createManual: (data) => request('/api/pedidos', { method: 'POST', body: JSON.stringify(data) }),
  upload: async (fichero, notas) => {
    const token = localStorage.getItem('token');
    const form = new FormData();
    form.append('fichero', fichero);
    if (notas) form.append('notas', notas);
    const r = await fetch(`${API_URL}/api/pedidos/upload`, {
      method: 'POST',
      headers: token ? { Authorization: `Bearer ${token}` } : {},
      body: form,
    });
    if (!r.ok) {
      let msg = `HTTP ${r.status}`;
      try { const j = await r.json(); msg = j.detail || msg; } catch {/* noop */}
      throw new Error(typeof msg === 'string' ? msg : JSON.stringify(msg));
    }
    return r.json();
  },
  updateHeader: (id, patch) => request(`/api/pedidos/${id}`, { method: 'PATCH', body: JSON.stringify(patch) }),
  updateLinea: (id, lineaId, patch) => request(`/api/pedidos/${id}/linea/${lineaId}`, { method: 'PATCH', body: JSON.stringify(patch) }),
  addLinea: (id, linea) => request(`/api/pedidos/${id}/linea/add`, { method: 'POST', body: JSON.stringify(linea) }),
  deleteLinea: (id, lineaId) => request(`/api/pedidos/${id}/linea/${lineaId}`, { method: 'DELETE' }),
  confirmar: (id) => request(`/api/pedidos/${id}/confirmar`, { method: 'POST' }),
  documentoUrl: (storageName) => `${API_URL}/api/pedidos/documento/${storageName}`,
};

// === Informes comerciales (Documento A/B) ===
export const informesApi = {
  analizar: (ventasFile, comisionesFile) => {
    // Multipart: fetch crudo con solo Authorization; el navegador pone el
    // Content-Type con boundary. NO usar req()/request() (fuerzan JSON → 422).
    const fd = new FormData();
    fd.append('ventas', ventasFile);
    fd.append('comisiones', comisionesFile);
    return fetch(`${API_URL}/api/informes/analizar`, {
      method: 'POST',
      headers: { Authorization: `Bearer ${localStorage.getItem('token')}` },
      body: fd,
    }).then(async (r) => { if (!r.ok) throw new Error((await r.json()).detail || 'Error'); return r.json(); });
  },
  generar: (job_id, tipos, codigos) =>
    req('/api/informes/generar', { method: 'POST', body: { job_id, tipos, codigos } }),
  familias: () => req('/api/informes/familias'),
  gruposSubir: (files) => {
    const fd = new FormData();
    [...files].forEach((f) => fd.append('archivos', f));
    return fetch(`${API_URL}/api/informes/grupos/subir`, {
      method: 'POST',
      headers: { Authorization: `Bearer ${localStorage.getItem('token')}` },
      body: fd,
    }).then(async (r) => { if (!r.ok) throw new Error((await r.json()).detail || 'Error'); return r.json(); });
  },
  gruposGenerar: (job_id, familia, modelos, items) =>
    req('/api/informes/grupos/generar', { method: 'POST', body: { job_id, familia, modelos, items } }),
  list: (params) => req(`/api/informes${qs(params)}`),
  facetas: () => req('/api/informes/facetas'),
  publicar: (id, publicado) =>
    req(`/api/informes/${id}/publicar`, { method: 'POST', body: { publicado } }),
  asignar: (id, usuario_destinatario) =>
    req(`/api/informes/${id}/asignar`, { method: 'POST', body: { usuario_destinatario } }),
  borrar: (id) => req(`/api/informes/${id}`, { method: 'DELETE' }),
  borrarMasivo: ({ ids, periodo }) =>
    req('/api/informes/borrar', { method: 'POST', body: { ids, periodo } }),
  publicarMasivo: ({ ids, periodo, publicado }) =>
    req('/api/informes/publicar-masivo', { method: 'POST', body: { ids, periodo, publicado } }),
  zip: async ({ ids, periodo, formato }) => {
    const token = localStorage.getItem('token');
    const res = await fetch(`${API_URL}/api/informes/zip`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', ...(token ? { Authorization: `Bearer ${token}` } : {}) },
      body: JSON.stringify({ ids, periodo, formato }),
    });
    if (!res.ok) throw new Error('No se pudo generar el ZIP');
    return res.blob();
  },
  descargar: async (id, formato) => {
    const token = localStorage.getItem('token');
    const res = await fetch(`${API_URL}/api/informes/${id}/descargar${formato ? `?formato=${formato}` : ''}`, {
      headers: token ? { Authorization: `Bearer ${token}` } : {},
    });
    if (!res.ok) throw new Error('No se pudo descargar el informe');
    return res.blob();
  },
};

// === Comisionistas (representantes ERP ↔ usuario CRM) ===
export const comisionistasApi = {
  list: (params) => req(`/api/comisionistas${qs(params)}`),
  sugerencias: (codigo) => req(`/api/comisionistas/${codigo}/sugerencias`),
  vincular: (codigo, usuario_id) =>
    req(`/api/comisionistas/${codigo}/vincular`, { method: 'PUT', body: { usuario_id } }),
  desvincular: (codigo) => req(`/api/comisionistas/${codigo}/vincular`, { method: 'DELETE' }),
};

export default { authApi, usuariosApi, auditApi, referenciaApi, healthApi };
